import { CartItem, CartTicket } from "@/interfaces/CartItem";
import { Order, PaymentMethodTypeEnum } from "@/interfaces/Order";
import { TicketType } from "@/interfaces/TicketType";
import { DefaultGetParams, GetParams, PriceType, payloadOrderInfosType } from "@/interfaces/Types.definition";
import axios from "@/tools/Axios/axios";
import { AppPrice } from "@/tools/Price";
import { RoomPlacement } from "@/tools/RoomPlacement/RoomPlacement";
import { Store, defineStore } from "pinia";
import { toast } from "vue3-toastify";
import { useEventStore } from "./EventStore";
import { useTicketTypeStore } from "./TicketTypeStore";
import { useUserStore } from "./UserStore";
import * as Sentry from "@sentry/browser";
import { Addon } from "@/interfaces/Addon";
import { IPaymentTransaction, IPaymentMethod } from "@/interfaces/PaymentTransaction";
import { getItemAction } from "./common/actions";
import { saveAs } from 'file-saver';
import { useTpeStore } from "./TpeStore";
import { useOrderStore } from "./OrderStore";
import { useQuantityDiscountStore } from "./QuantityDiscountStore";
import { computeQuantityDiscountLines, QuantityDiscountLine, sumQuantityDiscountLines } from "@/helpers/quantityDiscountResolver";

const INSURANCE_CUTOFF_MS = 3600000 // 1 heure en ms

export interface InsurancePrice {
    amount: number
    currency: string
    floated: number
    formatted: string
    locale: string
}

export interface CartState {
    order: Order | null,
    seatHoldToken: string | null,
    isProduct: boolean,
    currentStep: string,
    items: CartItem[],
    promocode: string | null,
    paymentMethod: IPaymentMethod | null,
    paymentTransactions: IPaymentTransaction[],
    isBookingSeats: boolean,
    serverTotalOverride: PriceType | null,
    insurance: InsurancePrice | null,
    insuranceChoice: 'yes' | 'no' | null
}

export const useCartStore = defineStore("CartStore", {
    state: (): CartState => {
        return {
            isProduct: false,
            seatHoldToken: null,
            order: null,
            currentStep: 'tickets',
            items: [],
            promocode: null,
            paymentTransactions: [],
            paymentMethod: null,
            isBookingSeats: false,
            serverTotalOverride: null,
            insurance: null,
            insuranceChoice: null
        }
    },
    actions: {
        async fetchOrder(orderId: string) {
            let params: GetParams = DefaultGetParams()
            params.populate = ['tickets', 'payments']
            await getItemAction<Order, Store<'CartStore'>>(`/events/orders/${orderId}`, params, this, 'order')
        },
        async nextStep() {
            const eventId = useEventStore().currentEvent._id
            if (eventId) {
                switch (this.$router.currentRoute.value.name) {
                    case 'ticket':
                    case 'ticket_seats':
                        this.$router.push({name: 'infos', params: { eventId }})
                        break;
                    case 'buvette':
                        this.$router.push({name: 'buvette_infos'})
                        break;
                    case 'buvette_infos':
                        this.$router.push({name: 'payment', params: { eventId }})
                        break;
                    case 'infos':
                        if (this.totalCart.amount === 0) {
                            this.paymentMethod = { method: 'free', customMethod: null }
                            const isPaid = await this.payOrder()
                            const orderId = this.order?._id
                            if (isPaid && orderId) {
                                this.$router.push({name: 'summary', params: { eventId, orderId }})
                            }
                        }
                        else if (this.shouldOfferInsurance()) {
                            this.$router.push({name: 'insurance', params: { eventId }})
                        }
                        else {
                            this.$router.push({name: 'payment', params: { eventId }})
                        }
                        break;
                    case 'insurance':
                        this.$router.push({name: 'payment', params: { eventId }})
                        break;
                    case 'payment':
                        const orderId = this.order._id
                        if (orderId) {
                            this.$router.push({name: 'summary', params: { eventId, orderId }})
                        }
                        break
                }
            }
        },
        shouldOfferInsurance(): boolean {
            const eventStore = useEventStore()
            const event = eventStore.currentEvent
            if (!event) return false

            const proposeInsurance = event.ticketOffice?.proposeInsurance === true
            const hasInsurance = event.ticketing?.hasInsurance === true
            if (!proposeInsurance || !hasInsurance) return false

            if (this.totalCart.amount <= 0) return false

            const startDate = eventStore.currentSession?.date ?? event.dates?.startDate
            if (!startDate) return false

            const diff = new Date(startDate).getTime() - Date.now()
            return diff > INSURANCE_CUTOFF_MS
        },
        async fetchInsurancePrice(): Promise<InsurancePrice | null> {
            // Ne touche pas à `this.insurance` : ce champ représente
            // l'assurance EFFECTIVEMENT souscrite (alimente le panier).
            // Tant que l'utilisateur n'a pas cliqué "Souscrire", le
            // total du Cart ne doit pas inclure l'assurance.
            if (!this.order?.token) return null
            try {
                const { data } = await axios.get(`/events/orders/insurance/price/${this.order.token}`)
                const price: InsurancePrice = data.amount
                return price
            } catch (error: any) {
                Sentry.captureException(error)
                return null
            }
        },
        /**
         * Sélection locale d'assurance (sans appel API). Utilisée par la
         * vue Insurance quand l'opérateur clique sur "Souscrire". L'appel
         * API se fait au moment du clic "Continuer" du panier (cf. Cart.vue
         * → confirmInsuranceChoice).
         *
         * Garde le prix en mémoire pour pouvoir afficher la ligne assurance
         * dans le panier mais sans encore la persister côté API.
         */
        selectInsurance(price: InsurancePrice) {
            this.insurance = price
            this.insuranceChoice = 'yes'
        },
        clearInsurance() {
            this.insurance = null
            this.insuranceChoice = 'no'
        },
        /**
         * Persiste le choix d'assurance auprès de l'API. Appelée par le
         * panier au moment du clic "Continuer" sur l'étape `insurance`.
         * Retourne `true` si la requête réussit (ou si rien à faire = pas
         * d'assurance choisie), `false` si l'appel échoue.
         *
         * Note : aucun appel API si l'opérateur a choisi "Pas d'assurance"
         * — l'order draft est créé sans assurance par défaut. On ne
         * propose pas pour l'instant la possibilité de retirer une
         * assurance qui aurait déjà été posée (cas qui ne se produit
         * plus avec ce flow puisque l'API n'est appelée qu'au Continuer).
         */
        async applyInsurance(): Promise<boolean> {
            if (this.insuranceChoice !== 'yes' || !this.insurance) {
                return true
            }
            if (!this.order?.token) return false
            try {
                const { status } = await axios.put(
                    `/events/orders/draft/${this.order.token}`,
                    {
                        insurance: { type: 'neat', amount: this.insurance.amount },
                    },
                )
                return status === 200
            } catch (error: any) {
                Sentry.captureException(error)
                return false
            }
        },
        async cancel() {
            await this.removeHoldToken()
            for (const item of this.items) {
                await this.deleteItem(item.ticketType)
            }
            this.$reset()
        },

        async removeHoldToken() {
            if (this.seatHoldToken) {
                try {
                    await new RoomPlacement().expireHoldToken(this.seatHoldToken)
                } catch (error: any) {
                    Sentry.captureException(error, { extra: error.cause })
                }
                this.seatHoldToken = null
            }
        },

        /**
         *
         * @param ticketType
         */
        addTicket(ticketType: TicketType, seat?: string) {
            const tempId = Math.random().toString(36).slice(2, 7);
            const existingCartItem = this.items.find((item: CartItem) => item.ticketType._id == ticketType._id)
            const cartTicket: CartTicket = {
                label: ticketType.name,
                _app: {
                    tempId,
                    forms: [],
                    addons: []
                }
            }
            if (seat) {
                cartTicket.seat = [seat]
            }
            if (existingCartItem) {
                existingCartItem.quantity += 1
                existingCartItem.tickets.push(cartTicket)
            }
            else {
                const newCartItem: CartItem = {
                    ticketType,
                    quantity: 1,
                    tickets: [cartTicket]
                }
                this.items.push(newCartItem)
            }
        },
        removeTicket(ticketType: TicketType, seat?: string) {
            const cartItemIndex = this.items.findIndex((item: CartItem) => item.ticketType._id == ticketType._id)
            if (cartItemIndex > -1) {
                const cartItem = this.items[cartItemIndex]
                if (cartItem.quantity == 1) {
                    this.items = this.items.filter((el: CartItem) => el.ticketType._id != ticketType._id)
                    return true
                }
                else {
                    this.items[cartItemIndex].quantity -= 1
                    if (seat) {
                        const ticketIndex = this.items[cartItemIndex].tickets.findIndex((cartTicket: CartTicket) => cartTicket.seat[0] == seat)
                        if (ticketIndex !== -1) {
                            delete this.items[cartItemIndex].tickets[ticketIndex]
                            return true
                        }
                    }
                    this.items[cartItemIndex].tickets.pop()
                    return true
                }
            }
            return false
        },
        async deleteItem(ticketType: TicketType) {
            const cartItemIndex = this.items.findIndex((item: CartItem) => item.ticketType._id == ticketType._id)
            if (cartItemIndex > -1) {
                this.items = this.items.filter((el: CartItem) => el.ticketType._id != ticketType._id)
            }
        },
        getItemPerTicketType(ticketType: TicketType): CartItem | undefined {
            return this.items.find((el: CartItem) => {
                return el.ticketType._id == ticketType._id
            })
        },
        async generatePdf(download: boolean = false): Promise<any> {
            try {
                let url: string
                let filename = `billets-${this.order.orderNumber}.pdf`
                const tickets = useOrderStore().tickets
                if (this.order.isProduct && tickets.length) {
                    url = `/events/orders/stream/ticketId/${tickets[0]._id}/${this.order.token}`
                    filename = `commande-${this.order.orderNumber}.pdf`
                }
                else {
                    url = `/events/orders/stream/orderIdTickets/${this.order._id}/${this.order.token}`
                }

                const pdf = await axios.get(url, {
                    responseType: 'blob'
                })

                if (download) {
                   saveAs(pdf.data, filename);
                }
                return pdf
            } catch (error) {
                Sentry.captureException(error)
                toast.error('Erreur lors de la généreration de votre PDF')
                return false
            }
        },
        async generateDraftOrder(isProduct: boolean = false): Promise<boolean> {
            const userStore = useUserStore()
            const eventStore = useEventStore()
            if (userStore.sellingSession) {
                const tickets = this.items.map(cartItem => {
                    return {
                        quantity: cartItem.quantity,
                        sessionId: (eventStore.currentSession) ? eventStore.currentSession._id : null,
                        ticketTypeId: cartItem.ticketType._id,
                        seat: cartItem.tickets.map(ticket => (Array.isArray(ticket.seat)) ? (ticket.seat)[0]: null).filter(seat => seat)
                    }
                })
                const url = `/events/orders/draft/${userStore.sellingSession.eventId._id}/ticket-office/${userStore.sellingSession._id}`
                // expectedTotal : total affiche au guichet (frais inclus), en centimes.
                // Sert a l'API pour detecter un ecart avec le total recalcule serveur
                // (log Sentry uniquement, pas de rejet). Preparation a la centralisation
                // future du calcul de panier.
                const expectedTotal = Math.round(this.totalCartWithFees.amount * 100)
                try {
                    const { data, status } = await axios.post(url, {
                        currency: eventStore.currentEvent.ticketing.defaultCurrencyId._id,
                        origin: 'ticketOffice',
                        isProduct,
                        promoCode: null,
                        sessionId: (eventStore.currentSession) ? eventStore.currentSession._id : null,
                        tickets,
                        expectedTotal
                    })
                    if (status == 201) {
                        this.order = data
                        this.order._app = {
                            paymentMethods: []
                        }
                        this.syncServerTotalSilently(expectedTotal)
                        return true
                    }
                    toast.error('Une erreur est survenue lors de la création de la commande')
                } catch (error: any) {
                    Sentry.captureException(error)
                    toast.error('Une erreur est survenue lors de la création de la commande')
                }
            }
            return false
        },
        /**
         * Aligne silencieusement l'affichage du total sur la valeur calculee
         * par le serveur si elle diverge du total local attendu. Aucun toast,
         * aucune alerte : purement une mise a jour d'affichage.
         */
        syncServerTotalSilently(expectedTotal: number) {
            const serverTotal = this.order?.totalPrice?.totalWithAdjustments
            if (typeof serverTotal !== 'number') {
                return
            }
            if (serverTotal !== expectedTotal) {
                this.serverTotalOverride = new AppPrice(serverTotal).divide100()
            }
            else {
                this.serverTotalOverride = null
            }
        },
        async updateOrderInfos(payload: payloadOrderInfosType): Promise<boolean> {
            if (this.order) {
                payload.sendingMethod = 'same'
                const url = `/events/orders/draft/${this.order.token}`
                if (payload.tickets) {
                    payload.tickets = payload.tickets.map((ticketPayload) => {
                        const cartTicket = this.getCartTicketByTempid(ticketPayload.tempId)
                        if (cartTicket) {
                            ticketPayload.addons = cartTicket._app.addons.map((addon: Addon) => addon._id)
                        }
                        return ticketPayload
                    })
                }

                try {
                    const { status } = await axios.put(url, {...payload})
                    if (status == 200) {
                        return true
                    }
                } catch (error: any) {
                    Sentry.captureException(error, { extra: {
                        apiRequest: JSON.stringify(payload),
                        apiResponse: JSON.stringify(error)
                    }})
                    toast.error('Une erreur est survenue')
                }
            }
            return false
        },
        async payOrderWithTpe(): Promise<boolean> {
            try {
                const total = this.totalCartWithFees.amount * 100
                const resultIntent = await useTpeStore().getPaymentIntent(this.order.token, total)
                if (resultIntent) {
                    const resultCollect = await useTpeStore().collectPayment()
                    return resultCollect
                }
            } catch (error) {
                toast.error('Une erreur est survenue lors du paiement')
                Sentry.captureException(error)
                return false
            }
            return true
        },

        async payOrder(): Promise<boolean> {
            if (this.paymentMethod) {
                if (this.paymentMethod.method == PaymentMethodTypeEnum.creditCardTerminal) {
                    return this.payOrderWithTpe()
                }
                try {
                    let url = `/events/orders/add-payment/${this.order.token}`
                    const customMethod = (this.paymentMethod.customMethod && this.paymentMethod.method == PaymentMethodTypeEnum.custom) ? {
                        label: this.paymentMethod.customMethod.label,
                        value: this.paymentMethod.customMethod.value
                    } : undefined
                    await axios.post(url, {
                        "method": this.paymentMethod.method,
                        "referer": "ticketoffice",
                        "customMethod": customMethod,
                    })
                    return true
                } catch (error) {
                    Sentry.captureException(error, { extra: error.cause })
                    toast.error('Une erreur est survenue')
                    return false
                }

            }
            return false
        },
        getCartTicketByTempid(tempId: string): CartTicket|false {
            let searchedCartTicket:CartTicket|false = false
            if (this.items) {
                this.items.forEach((cartItem: CartItem) => {
                    const found = cartItem.tickets.find((cartTicket: CartTicket) => cartTicket._app.tempId == tempId)
                    if (found) {
                        searchedCartTicket = found
                    }
                })
            }
            return searchedCartTicket
        },
        async releaseCurrentsSeats(): Promise<boolean> {
            if (useEventStore().isRoomPlacement) {
                const secret = useUserStore().organisation?.seatsIoWorkspaceSecret
                if (secret) {
                    const eventId = useEventStore().roomPlacementEventId
                    if (eventId) {
                        try {
                            return await new RoomPlacement().releaseAllSeats(this.items)
                        } catch (error: any) {
                            Sentry.captureException(error, { extra: error.cause })
                            toast.error('Une erreur est survenue lors de la libération des places')
                            return false
                        }
                    }
                }
            }
            return false
        },
        async holdManualPlaces(): Promise<boolean> {
            const secret = useUserStore().organisation?.seatsIoWorkspaceSecret
            if (secret && useEventStore().isRoomPlacement) {}
                const eventId = useEventStore().roomPlacementEventId
                if (eventId) {
                    try {
                        this.isBookingSeats = true
                        const holdToken = await new RoomPlacement().generateHoldToken()
                        this.seatHoldToken = holdToken.holdToken
                        return await new RoomPlacement().holdManualSeats(this.items, holdToken)
                    } catch (error: any) {
                        Sentry.captureException(error, { extra: error.cause })
                        toast.error("Impossible de réserver les places sélectionnées")
                        this.isBookingSeats = false
                        return false
                    }
                }
        },
        async holdAvailablePlaces(): Promise<boolean> {
            const secret = useUserStore().organisation?.seatsIoWorkspaceSecret
            if (secret && useEventStore().isRoomPlacement) {
                const eventId = useEventStore().roomPlacementEventId
                if (eventId) {
                    try {
                        const holdToken = await new RoomPlacement().generateHoldToken()
                        this.seatHoldToken = holdToken.holdToken
                        return await new RoomPlacement().holdSeats(this.items, useTicketTypeStore().categories, holdToken)
                    } catch (error: any) {
                        Sentry.captureException(error, { extra: error.cause })
                        toast.error("Problème avec le placement automatique. Veuillez utiliser le placement manuel")
                        await this.releaseCurrentsSeats()
                        return false
                    }
                }
            }
            return false
        }
    },
    getters: {
        insuranceAmount(): number {
            return this.insurance?.floated ?? 0
        },
        // Retourne l'OrderAdjustment de type editionFees présent sur le
        // draft courant, ou null si absent. Lecture depuis
        // order.totalPrice.adjustments (source de vérité API), en filtrant
        // sur le type "editionFees" (valeur enum côté API).
        getEditionFees(): { type?: string; amount: number; label?: string } | null {
            const adjustments = this.order?.totalPrice?.adjustments
            if (!adjustments) return null
            return adjustments.find(a => a.type === 'editionFees') ?? null
        },
        // Montant des frais d'édition en euros flottants.
        // Source 1 (post-draft) : adjustment réel issu de l'order API.
        // Source 2 (pre-draft)  : config event.editionFees si éligible.
        // Retourne 0 dans tous les autres cas.
        // C'est CE getter qui doit être utilisé partout pour l'affichage
        // (panier, total) car il couvre les deux phases du checkout.
        editionFeesPreviewAmount(): number {
            // Phase post-draft : la source de vérité est l'adjustment API.
            if (this.order) {
                const adj = this.getEditionFees
                if (adj) {
                    return new AppPrice(adj.amount).divide100().amount
                }
                // Draft créé mais pas d'adjustment editionFees → event sans FDS
                // ou commande exclue (import, gratuite). On renvoie 0 sans
                // regarder la config event pour rester cohérent avec l'API.
                return 0
            }
            // Phase pre-draft : lecture depuis la config de l'event.
            const event = useEventStore().currentEvent
            if (!event?.editionFees?.enabled) return 0
            if (!event.editionFees.amount || event.editionFees.amount <= 0) return 0
            // Pas de FDS si le panier est vide ou totalement gratuit.
            if (this.totalCart.amount <= 0) return 0
            return new AppPrice(event.editionFees.amount).divide100().amount
        },
        // Montant des frais d'édition en euros flottants (comme
        // insuranceAmount). Retourne 0 si l'adjustment est absent.
        // @deprecated Utiliser editionFeesPreviewAmount à la place.
        editionFeesAmount(): number {
            const adj = this.getEditionFees
            if (!adj) return 0
            // L'amount est en centimes côté API (comme sellingPrice).
            return new AppPrice(adj.amount).divide100().amount
        },
        // Label à afficher pour les frais d'édition.
        // Source 1 (post-draft) : label de l'adjustment API.
        // Source 2 (pre-draft)  : label depuis event.editionFees.
        // Retourne null si aucune FDS applicable.
        editionFeesLabel(): string | null {
            if (this.order) {
                const adj = this.getEditionFees
                return adj?.label ?? null
            }
            const event = useEventStore().currentEvent
            return event?.editionFees?.label ?? null
        },
        // Quota par commande effectif côté guichet.
        // Retourne null si la limite ne s'applique pas au guichet
        // (flag quotaPerOrderTicketOffice absent ou false, ou quota non défini).
        quotaPerOrderEffective(): number | null {
            const t = useEventStore().currentEvent?.ticketing
            if (!t?.quotaPerOrderTicketOffice) return null
            return t.quotaPerOrder ?? null
        },
        // Nombre total de billets dans le panier (hors produits buvette).
        currentCartTotal(): number {
            return this.items.reduce((acc, item: CartItem) => {
                if (item.ticketType.isProduct) return acc
                return acc + item.quantity
            }, 0)
        },
        // Places restantes avant d'atteindre le quota.
        // Retourne Infinity si aucun quota n'est configuré.
        remainingQuota(): number {
            const quota = this.quotaPerOrderEffective
            if (quota === null) return Infinity
            return Math.max(0, quota - this.currentCartTotal)
        },
        // Vrai si le quota est atteint (et qu'un quota est configuré).
        isQuotaReached(): boolean {
            const quota = this.quotaPerOrderEffective
            if (quota === null) return false
            return this.currentCartTotal >= quota
        },
        itemsQty(): number {
            return this.items.reduce((acc, item: CartItem) => {
                return acc + item.tickets.length
            }, 0)
        },
        isEmpty():boolean {
            return (this.items.length == 0)
        },
        totalFees(): PriceType|false {
            // Au guichet, la commission est toujours en mode incluse
            // (déduite du costPrice côté API), jamais majorée sur le prix affiché.
            // Donc pas de frais de service supplémentaires à afficher.
            return new AppPrice(0).divide100()
        },
        totalCartWithFees() {
            // Priorite au total serveur si disponible (cf.
            // syncServerTotalSilently). C'est la source de verite
            // apres creation du draft, inclut deja frais/assurance/
            // frais d'edition.
            if (this.serverTotalOverride) {
                return this.serverTotalOverride
            }
            const fees = this.totalFees ? this.totalFees.amount : 0
            const total = fees + this.totalCart.amount + this.insuranceAmount + this.editionFeesPreviewAmount
            return new AppPrice(total)
        },
        totalCart(): PriceType {
            if (this.serverTotalOverride) {
                return this.serverTotalOverride
            }
            const totalTicket: number = this.items.reduce((acc, item: CartItem) => acc + (item.quantity * Number(item.ticketType.price)), 0)
            let totalAddons = 0
            this.items.forEach((cartItem: CartItem) => {
                cartItem.tickets.forEach((cartTicket: CartTicket) => {
                    if (cartTicket._app.addons) {
                        cartTicket._app.addons.forEach((addon: Addon) => {
                            totalAddons += addon.price
                        })
                    }
                })
            })
            // Plan 039 : retire la somme des remises quantitatives.
            // Aper�u client uniquement, le total definitif viendra du
            // serveur (serverTotalOverride) apres creation du draft.
            const total = totalTicket + totalAddons - this.quantityDiscountReduction
            return new AppPrice(Math.max(0, total)).divide100()
        },
        /**
         * Aper�u des lignes de remise quantitative applicables au
         * panier courant. Une ligne par regle (le seuil s'evalue sur
         * la somme des billets d'une meme regle, tous tarifs concernes
         * confondus). Utilise par le composant Cart pour afficher
         * une ligne "Remise : <label> -X,XX �" avant les frais.
         *
         * Cote guichet, les prix des tickets sont en CENTIMES (comme
         * l'API). On produit une paire {_id, price} par billet du
         * panier (un item avec quantity=3 → 3 entrees) pour que le
         * seuil s'evalue correctement.
         */
        quantityDiscountLines(): QuantityDiscountLine[] {
            const rules = useQuantityDiscountStore().rules
            if (!rules || rules.length === 0) return []
            const tickets: Array<{ _id: string; price: number }> = []
            this.items.forEach((cartItem: CartItem) => {
                for (let i = 0; i < cartItem.quantity; i++) {
                    tickets.push({
                        _id: cartItem.ticketType._id,
                        price: Number(cartItem.ticketType.price) || 0,
                    })
                }
            })
            return computeQuantityDiscountLines(rules, tickets)
        },
        /**
         * Somme des remises quantitatives applicables (en centimes).
         * Utilise dans le calcul de totalCart.
         */
        quantityDiscountReduction(): number {
            return sumQuantityDiscountLines(this.quantityDiscountLines)
        },
        totalPaid(): PriceType {
            // Base = totalCartWithFees (qui inclut l'assurance
            // quand elle est souscrite). Avant ce fix, `totalCart`
            // était utilisé → l'assurance n'entrait pas dans le
            // montant payé → la commande passait en "partiellement
            // payée" si l'assurance était souscrite.
            let amount = 0
            if (this.paymentMethod) {
                amount = this.totalCartWithFees.amount * 100
            }
            return new AppPrice(amount).divide100()
        },
        leftToPay(): PriceType {
            const amount = this.totalCartWithFees.amount - this.totalPaid.amount
            return new AppPrice(amount)
        },
        isOrderExpired(): boolean {
            if (this.order) {
                const expire = new Date(this.order.expire).getTime();
				const now = new Date().getTime();
                if (expire > now) {
                    return false
                }
            }
            return false
        }
    },

})
